import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatIcon from "@mui/icons-material/Chat";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Avatar, Box, Button, Chip, CircularProgress, Grid, Menu, MenuItem, Stack, Typography, useMediaQuery } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useModals } from "../../../../../../context/ModalContext";
import { formatHHMM, formatStatus } from "../../../../../../utils";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import { ConfirmDialog } from "../../../../../common/ConfirmDialog";
import { NewBadge } from "../../../../../common/NewBadge";
import { useGetInvoice } from "../../../../../invoice/view/components/ClientView/hooks/useGetInvoice";
import { InvoiceDetailsView } from "../../../../../invoice/view/components/DetailsView";
import { InvoiceListModalContent } from "../../../../../invoice/view/components/InvoiceListModalContent";
import { useGetInvoicesByJobEvent } from "../../../../../invoice/view/hooks/useGetInvoicesByJobEvent";
import { getInvoiceStatus, getInvoiceStatusLabel } from "../../../../../invoice/view/utils/getInvoiceStatus";
import { colors, alphaVar } from "../../../../../ui/colors";
import { WorkforceMember } from "../../../../interfaces/job.interfaces";
import { useDeleteJobEventAction } from "../../../../jobs-list/job-card/hooks/useDeleteJobEventAction";
import { JobLayout } from "../../../../jobs-list/jobs-layout/Job.layout";
import { JobsEventsEdit } from "../../edit/JobsEventsEdit";
import { getStatusColor } from "../const/status.styles.const";
import { AnimatePresence, motion } from "framer-motion";
import { useWatchInvoiceNotifications } from "../../../../../../notifications/hooks/useWatchInvoiceNotifications";
import type { IInvoice } from "../../../../../invoice/create/interfaces/invoice.interfaces";
import { InvoiceCreateView } from "../../../../../invoice/create/InvoiceCreateView";
import { getFallbackInvoiceTotalFromJob } from "../../../../../invoice/create/utils/quoteJobInvoiceTotals";
import type { Job } from "../../../../interfaces/job.interfaces";
import { type JobEventForInvoice, useJobEventsBoard } from "../context";
import { JobEventMultilpeInvoiceCheck } from "./JobEventMultilpeInvoiceCheck";
import { Edit, KeyboardArrowDown } from "@mui/icons-material";
import { JobApproved } from "@components/jobs/jobs-list/jobs-layout/components/Approved/JobApproved";
import { JobEventBulkWorkforceCheck } from "./JobEventBulkWorkforceCheck";

type JobEventTime = {
  start: string | Date;
  end: string | Date;
};

function toInvoiceJobEvent(jobEventUid: string, time: JobEventTime | undefined, status: string): JobEventForInvoice {
  const start = time?.start;
  const end = time?.end;
  const startDate = !start ? "" : typeof start === "string" ? start : start.toISOString();
  const endDate = !end ? "" : typeof end === "string" ? end : end.toISOString();
  return { uid: jobEventUid, startDate, endDate, status };
}

type JobEventRowProps = {
  time?: JobEventTime;
  status: string;
  isNew?: boolean;
  workforce?: WorkforceMember[];
  jobEventUid: string;
  eventSchedulerUid: string;
  clientUid: string;
  scopeOfWorkUid: string;
  jobUid: number;
  invoicePreviewModalKey?: string;
  invoiceListModalKey?: string;
  billingInvoiceUid?: string | null;
  wrapLeftPart: (content: React.ReactElement) => React.ReactElement;
  /** Parent job (same for all rows); used to show quote-based estimate in the row. */
  job?: Job | null;
  /** When true, show the job-level price line (avoid repeating on every visit row). */
  showJobPricing?: boolean;
  isFocused?: boolean;
  availabilityWindow?: { startAt: string; endAt: string; excludeVisitUid?: string } | null;
};

export function JobEventRow({
  time,
  status,
  isNew = false,
  workforce = [],
  jobUid,
  jobEventUid,
  eventSchedulerUid,
  clientUid,
  scopeOfWorkUid,
  invoicePreviewModalKey,
  invoiceListModalKey,
  billingInvoiceUid,
  wrapLeftPart,
  job,
  showJobPricing,
  isFocused,
  availabilityWindow = null,
}: JobEventRowProps) {
  const isXs = useMediaQuery("(max-width: 600px)");
  const { openModal, closeByKey, modals } = useModals();
  const { isCreateInvoiceForMultipleVisitsOpen, isBulkWorkforceOpen } = useJobEventsBoard();

  const previousInvoiceRef = useRef<any>(null);
  const hasOpenedDetailsRef = useRef(false);
  const wasCreateModalOpenRef = useRef(false);
  const modalsRef = useRef(modals);
  const isMountedRef = useRef(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deleteEventMutation = useDeleteJobEventAction();

  useEffect(() => {
    modalsRef.current = modals;
  }, [modals]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const statusColor = getStatusColor(status);
  const queryClient = useQueryClient();
  const { data: invoiceByJobEvent, isLoading: isLoadingInvoiceJob } = useGetInvoice({
    jobEventsUids: [jobEventUid],
  });

  const { data: invoiceByBillingUid, isLoading: isLoadingInvoiceBilling } = useGetInvoice(billingInvoiceUid ?? "");
  const { data: invoicesByEvent = [], isLoading: isLoadingList } = useGetInvoicesByJobEvent(jobEventUid);

  /** Prefer list, then invoice UID from the visit row, then legacy GET ?jobEventUid= */
  const resolvedInvoice = invoicesByEvent?.[0] ?? invoiceByBillingUid ?? invoiceByJobEvent;
  const invoice = resolvedInvoice;

  useWatchInvoiceNotifications({
    onNotificationArrived: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", "by-job-event", jobEventUid] });
      queryClient.invalidateQueries({ queryKey: ["invoice", jobEventUid] });
      if (billingInvoiceUid) {
        queryClient.invalidateQueries({ queryKey: ["invoice", billingInvoiceUid] });
      }
    },
  });

  useEffect(() => {
    const createModalKey = `Dashboard-InvoiceCreate-${jobEventUid}`;
    wasCreateModalOpenRef.current = modals.some((m) => m.key === createModalKey && !m.minimized);
  }, [modals, jobEventUid]);

  useEffect(() => {
    const previousInvoice = previousInvoiceRef.current;
    const currentInvoice = invoice;
    const isNewInvoice = !previousInvoice && currentInvoice;
    const isDifferentInvoice = previousInvoice && currentInvoice && previousInvoice.uid !== currentInvoice.uid;
    const createModalKey = `Dashboard-InvoiceCreate-${jobEventUid}`;
    const isCreateModalOpen = modalsRef.current.some((m) => m.key === createModalKey && !m.minimized);
    const wasCreateModalOpen = wasCreateModalOpenRef.current;

    const shouldAutoOpen =
      (isNewInvoice || isDifferentInvoice) &&
      (isCreateModalOpen || wasCreateModalOpen) &&
      !hasOpenedDetailsRef.current &&
      currentInvoice &&
      isMountedRef.current;

    if (shouldAutoOpen && invoicePreviewModalKey) {
      const modalKey = invoicePreviewModalKey;

      if (!modalsRef.current.some((m) => m.key === modalKey && !m.minimized)) {
        hasOpenedDetailsRef.current = true;
        if (isCreateModalOpen) closeByKey(createModalKey);
        const timeoutId = setTimeout(() => {
          if (currentInvoice) {
            openModal(
              { key: modalKey, preventParentTrigger: true },
              <InvoiceDetailsView
                invoiceUid={currentInvoice.uid}
                scopeOfWorkUid={scopeOfWorkUid}
                invoicePreviewModalKey={invoicePreviewModalKey}
                invoiceListModalKey={invoiceListModalKey}
                jobEventUid={jobEventUid}
              />,
              null,
            );
          }
        }, 150);
        return () => clearTimeout(timeoutId);
      } else {
        hasOpenedDetailsRef.current = true;
      }
    }

    if (currentInvoice) {
      if (previousInvoice && previousInvoice.uid !== currentInvoice.uid) hasOpenedDetailsRef.current = false;
      previousInvoiceRef.current = currentInvoice;
    } else {
      previousInvoiceRef.current = null;
      hasOpenedDetailsRef.current = false;
    }
  }, [invoice, jobEventUid, scopeOfWorkUid, openModal, closeByKey, invoicePreviewModalKey]);

  const createdDate = (() => {
    if (!time?.start) return null;

    const d = typeof time.start === "string" ? new Date(time.start) : time.start;

    return Number.isFinite(d.getTime()) ? d : null;
  })();

  const startDateLabel = createdDate
    ? new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "2-digit" }).format(createdDate)
    : "Pending date";

  const hasWorkforce = workforce.length > 0;
  const workersToChange = workforce.filter((person) => person.reassignment);
  const timeLabel = time?.start && time?.end ? `${formatHHMM(time.start)} - ${formatHHMM(time.end)}` : "Pending time";
  const invoiceJobEvent = toInvoiceJobEvent(jobEventUid, time, status);
  const invoiceBlocksNewBilling = (inv: typeof resolvedInvoice) => !!inv && (getInvoiceStatus(inv) === "UNPAID" || getInvoiceStatus(inv) === "PAID");

  const hasStaleBillingLink =
    Boolean(billingInvoiceUid) &&
    !resolvedInvoice &&
    invoicesByEvent.length === 0 &&
    !isLoadingList &&
    !isLoadingInvoiceBilling &&
    !isLoadingInvoiceJob;

  const hasActiveInvoice =
    invoicesByEvent.some((i) => {
      const s = getInvoiceStatus(i);
      return s === "UNPAID" || s === "PAID";
    }) ||
    invoiceBlocksNewBilling(resolvedInvoice) ||
    hasStaleBillingLink;

  const isDisabledRow = isCreateInvoiceForMultipleVisitsOpen && hasActiveInvoice;
  const isSelectionModeOpen = isCreateInvoiceForMultipleVisitsOpen || isBulkWorkforceOpen;
  const isEditDisabled = ["completed", "cancelled"].includes(status);

  const renderLeftContent = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          backgroundColor: alphaVar(colors.success[100], 0.65),
          border: `1px solid ${alphaVar(colors.success[500], 0.16)}`,
          color: colors.success[600],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <CalendarMonthRoundedIcon sx={{ fontSize: 19 }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.84rem", sm: "0.9rem" },
              fontWeight: 800,
              color: colors.gray[800],
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            {startDateLabel}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              color: colors.gray[400],
            }}
          >
            {timeLabel}
          </Typography>

          <Chip
            className="status-chip"
            size="small"
            label={formatStatus(status)}
            sx={{
              height: 23,
              fontSize: 11,
              fontWeight: 700,
              borderRadius: "999px",
              backgroundColor: alphaVar(statusColor.color, 0.07),
              color: alphaVar(statusColor.color, 0.82),
              border: `1px solid ${alphaVar(statusColor.color, 0.18)}`,
              transition: "all 120ms ease-in-out",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />

          {workersToChange.length > 0 && (
            <Chip
              size="small"
              icon={<WarningRoundedIcon sx={{ fontSize: 14, color: "#b91c1c !important" }} />}
              label={workersToChange.length === 1 ? "1 worker must be reassigned" : `${workersToChange.length} workers must be reassigned`}
              sx={{
                height: 23,
                fontSize: 11,
                fontWeight: 800,
                borderRadius: "999px",
                backgroundColor: "#fee2e2",
                color: "#b91c1c",
                border: "1px solid #fecaca",
              }}
            />
          )}

          {invoice?.parentDepositInvoiceUid && (
            <Chip
              size="small"
              icon={<LocalOfferIcon sx={{ fontSize: 11, color: `${colors.success[700]} !important` }} />}
              label={`Deposit Applied: ${new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(invoice.creditedDepositAmount ?? 0)}`}
              sx={{
                height: 23,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: "999px",
                backgroundColor: alphaVar(colors.success[100], 0.75),
                color: colors.success[700],
                border: `1px solid ${alphaVar(colors.success[500], 0.18)}`,
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            mt: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          {job && showJobPricing ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.65,
                px: 1,
                py: 0.4,
                borderRadius: "999px",
                backgroundColor: alphaVar(colors.orange[100], 0.55),
                border: `1px solid ${alphaVar(colors.orange[400], 0.12)}`,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.orange[700],
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  fontSize: "0.68rem",
                }}
              >
                Job total
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: colors.gray[800],
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  fontSize: "0.7rem",
                }}
              >
                {(() => {
                  const value = getFallbackInvoiceTotalFromJob(job);

                  return value > 0
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(value)
                    : "—";
                })()}
              </Typography>
            </Box>
          ) : null}

          {hasWorkforce ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.85,
                minWidth: 0,
                flex: 1,
              }}
            >
              <Stack direction="row" spacing={-0.75} sx={{ flexShrink: 0 }}>
                {workforce.map((person) => (
                  <Avatar
                    key={person.uid}
                    sx={{
                      width: 27,
                      height: 27,
                      fontSize: 12,
                      fontWeight: 800,
                      border: person.reassignment ? "2px solid #b91c1c" : "2px solid var(--fx-surface-base, #FFFFFF)",
                      bgcolor: person.reassignment ? "#b91c1c" : colors.gray[300],
                      color: "#fff",
                      boxShadow: "0 3px 10px rgba(15, 23, 42, 0.14)",
                    }}
                  >
                    {person.firstName.charAt(0)}
                  </Avatar>
                ))}
              </Stack>

              <Typography
                className="workforce-names"
                variant="body2"
                sx={{
                  color: colors.gray[400],
                  fontWeight: 600,
                  fontSize: "0.74rem",
                  transition: "color 120ms ease-in-out",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {workforce.map((person, index) => (
                  <Box
                    key={person.uid}
                    component="span"
                    title={person.reassignment?.reason || ""}
                    sx={{ color: person.reassignment ? "#b91c1c" : "inherit", fontWeight: person.reassignment ? 800 : 600 }}
                  >
                    {person.firstName} {person.lastName}{index < workforce.length - 1 ? ", " : ""}
                  </Box>
                ))}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.65,
                px: 1,
                py: 0.45,
                borderRadius: "999px",
                backgroundColor: alphaVar(colors.warning[100], 0.6),
                border: `1px solid ${alphaVar(colors.warning[400], 0.14)}`,
              }}
            >
              <WarningRoundedIcon sx={{ fontSize: 15, color: colors.warning[500] }} />

              <Typography
                variant="body2"
                sx={{
                  color: colors.warning[600],
                  fontWeight: 800,
                  fontSize: "0.72rem",
                }}
              >
                Assign workforce
              </Typography>
            </Box>
          )}

          <BaseModalComponent
            modalKey={`Dashboard-JobChat-${jobEventUid}`}
            modalLabel={`Job ${job?.id ?? jobUid}`}
            preventParentTrigger
            elementTrigger={
              <Button
                variant="text"
                size="small"
                startIcon={<ChatIcon sx={{ color: colors.orange[600], fontSize: "14px !important" }} />}
                sx={{
                  minWidth: 0,
                  px: 0.85,
                  py: 0.35,
                  borderRadius: 1.5,
                  color: colors.gray[500],
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  textTransform: "none",
                  flexShrink: 0,
                  "&:hover": {
                    color: colors.orange[600],
                    bgcolor: alphaVar(colors.orange[100], 0.45),
                  },
                }}
              >
                Job Chat
              </Button>
            }
          >
            <JobLayout job={job} currentEventUid={jobEventUid} disableDetailsButton focusJobChat />
          </BaseModalComponent>
        </Box>
      </Box>
    </Box>
  );

  const [anchorEl, setAnchorEl] = React.useState(null);
  const editEventBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const editWorkforceBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const editEventModalKey = `Dashboard-EditEvent-${jobEventUid}`;
  const editWorkforceModalKey = `Edit-Workforce-${jobEventUid}`;
  const handleMenuClose = () => setAnchorEl(null);

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.5, sm: 1.4 },
        cursor: "pointer",
        position: "relative",
        borderRadius: 3,
        border: `1px solid ${isFocused ? alphaVar(colors.orange.base, 0.28) : colors.gray[100]}`,
        bgcolor: isFocused ? alphaVar(colors.orange.base, 0.04) : "transparent",
        boxShadow: isFocused ? "0 6px 18px rgba(15, 23, 42, 0.08)" : "none",
        transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
        ...(isFocused && {
          "& .workforce-names": {
            color: colors.gray[700],
          },
          "& .status-chip": {
            backgroundColor: alphaVar(statusColor.color, 0.08),
            borderColor: alphaVar(statusColor.color, 0.28),
            color: statusColor.color,
          },
        }),

        ...(!isDisabledRow && {
          "&:hover": {
            bgcolor: alphaVar(colors.orange.base, 0.025),
            borderColor: alphaVar(colors.orange.base, 0.22),
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
          },
          "&:hover .workforce-names": {
            color: colors.gray[700],
          },
          "&:hover .status-chip": {
            backgroundColor: alphaVar(statusColor.color, 0.08),
            borderColor: alphaVar(statusColor.color, 0.28),
            color: statusColor.color,
          },
        }),
      }}
    >
      {isNew && <NewBadge />}
      {wrapLeftPart(
        <Grid container alignItems="center" spacing={isXs ? 2 : 0}>
          <Grid
            item
            xs={12}
            sm={6}
            sx={
              isDisabledRow
                ? {
                    pointerEvents: "none",
                    opacity: 0.45,
                    transition: "opacity 120ms ease-in-out",
                  }
                : undefined
            }
          >
            <motion.div
              key={isSelectionModeOpen ? "multi" : "single"}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, width: "100%" }}
            >
              {isCreateInvoiceForMultipleVisitsOpen && <JobEventMultilpeInvoiceCheck jobEvent={invoiceJobEvent} disabled={hasActiveInvoice} />}

              {isBulkWorkforceOpen && <JobEventBulkWorkforceCheck jobEvent={invoiceJobEvent} />}
              {renderLeftContent()}
            </motion.div>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              pl: { xs: 6, sm: 0 },
              pointerEvents: "auto",
              position: "relative",
              zIndex: isDisabledRow ? 2 : undefined,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                ml: "auto",
                gap: 0.9,
                overflow: "visible",
                flexWrap: "nowrap",
              }}
            >
              {(() => {
                const showPillLoading =
                  isLoadingList || (invoicesByEvent.length === 0 && !resolvedInvoice && (isLoadingInvoiceBilling || isLoadingInvoiceJob));
                const showInvoicePill = invoicesByEvent.length > 0 || Boolean(billingInvoiceUid) || Boolean(resolvedInvoice);

                if (showPillLoading) {
                  return <CircularProgress size={20} sx={{ color: colors.gray[300] }} />;
                }

                if (showInvoicePill) {
                  return (
                    <InvoicePill
                      invoicesByEvent={invoicesByEvent}
                      billingInvoiceUid={billingInvoiceUid}
                      latestInvoice={resolvedInvoice}
                      onOpen={(e) => {
                        e.stopPropagation();
                        if (!invoiceListModalKey || !invoicePreviewModalKey) return;
                        openModal(
                          { key: invoiceListModalKey, preventParentTrigger: true },
                          <InvoiceListModalContent
                            jobEventUid={jobEventUid}
                            invoiceJobEvent={invoiceJobEvent}
                            scopeOfWorkUid={scopeOfWorkUid}
                            invoicePreviewModalKey={invoicePreviewModalKey}
                            invoiceListModalKey={invoiceListModalKey}
                            createModalKey={`Dashboard-InvoiceCreate-${jobEventUid}`}
                            startDateLabel={startDateLabel}
                            jobUid={jobUid}
                            clientUid={clientUid}
                            eventSchedulerUid={eventSchedulerUid}
                            billingInvoiceUid={billingInvoiceUid}
                          />,
                          null,
                        );
                      }}
                    />
                  );
                }

                return (
                  <AnimatePresence initial={false}>
                    {!isSelectionModeOpen && (
                      <motion.div
                        key="create-invoice-btn"
                        initial={{ opacity: 0, scale: 0.85, x: 20 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.85, x: 20 }}
                        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                        style={{ flexShrink: 0 }}
                      >
                        <BaseModalComponent
                          modalKey={`Dashboard-InvoiceCreate-${jobEventUid}`}
                          modalLabel="Create Invoice"
                          defaultSizes={{ width: "80%", height: "85%" }}
                          preventParentTrigger
                          elementTrigger={
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<AddRoundedIcon sx={{ fontSize: "15px !important" }} />}
                              sx={{
                                height: 32,
                                borderRadius: "8px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                color: colors.orange[600],
                                bgcolor: alphaVar(colors.orange[100], 0.5),
                                px: 1.5,
                                transition: "all 0.15s ease",
                                "&:hover": { bgcolor: colors.orange[100], color: colors.orange[700] },
                                "&:active": { transform: "scale(0.97)" },
                              }}
                            >
                              Invoice
                            </Button>
                          }
                        >
                          <InvoiceCreateView
                            jobUid={jobUid}
                            jobEvents={[invoiceJobEvent]}
                            clientUid={clientUid}
                            scopeOfWorkUid={scopeOfWorkUid}
                            eventSchedulerUid={eventSchedulerUid}
                            visitGroups={[
                              {
                                jobId: jobUid,
                                jobLabel: job?.alias ? `Job #${jobUid} — ${job.alias}` : `Job #${jobUid}`,
                                eventUids: [invoiceJobEvent.uid].filter((uid): uid is string => Boolean(uid)),
                              },
                            ]}
                          />
                        </BaseModalComponent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                );
              })()}

              {/*Listado de edicion*/}
              <AnimatePresence initial={false}>
                {!isSelectionModeOpen && !isEditDisabled && (
                  <motion.div
                    key="edit-event-menu-container"
                    initial={{ opacity: 0, scale: 0.85, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.85, x: 20 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1], delay: 0.04 }}
                    style={{ flexShrink: 0 }}
                  >
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                      }}
                      startIcon={<Edit fontSize="small" />}
                      endIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
                      sx={{
                        height: 32,
                        px: 1.5,
                        gap: 0.5,
                        borderRadius: "8px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "none",
                        color: colors.blue[700],
                        bgcolor: alphaVar(colors.blue[100], 0.5),
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: colors.blue[100],
                        },
                        "&:active": { transform: "scale(0.97)" },
                        "& .MuiButton-startIcon": { margin: 0 },
                        "& .MuiButton-endIcon": { margin: 0 },
                      }}
                    >
                      Actions
                    </Button>

                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
                      <MenuItem
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMenuClose();

                          setTimeout(() => {
                            editEventBtnRef.current?.click();
                          }, 0);
                        }}
                      >
                        Edit Event
                      </MenuItem>

                      <MenuItem
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMenuClose();

                          setTimeout(() => {
                            editWorkforceBtnRef.current?.click();
                          }, 0);
                        }}
                      >
                        Edit Workforce
                      </MenuItem>
                    </Menu>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Grid>
        </Grid>,
      )}

      <Box
        sx={{
          position: "fixed",
          width: 1,
          height: 1,
          top: -9999,
          left: -9999,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <BaseModalComponent
          modalLabel="Edit Event"
          modalKey={editEventModalKey}
          elementTrigger={
            <button ref={editEventBtnRef} type="button">
              Edit Event
            </button>
          }
        >
          <JobsEventsEdit jobEventUid={jobEventUid} />
        </BaseModalComponent>

        <BaseModalComponent
          modalLabel="Edit Workforce"
          modalKey={editWorkforceModalKey}
          elementTrigger={
            <button ref={editWorkforceBtnRef} type="button">
              Edit Workforce
            </button>
          }
        >
          <JobApproved
            key={job?.updatedAt}
            data={job}
            mode="edit"
            currentWf={workforce}
            availabilityWindow={availabilityWindow}
            jobEventUid={jobEventUid}
            onSaveWorkforce={async () => {
              await queryClient.invalidateQueries();
              closeByKey(editWorkforceModalKey);
            }}
          />
        </BaseModalComponent>
      </Box>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete Event"
        description="Are you sure you want to delete this event? This action cannot be undone."
        confirmText="Delete"
        onConfirm={() => {
          deleteEventMutation.mutate(jobEventUid);
          setIsDeleteDialogOpen(false);
        }}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />
    </Box>
  );
}

const statusPillColor = (s: ReturnType<typeof getInvoiceStatus>) => {
  switch (s) {
    case "PAID":
      return { bg: colors.success[100], fg: colors.success[700], dot: colors.success[500] };
    case "UNPAID":
      return { bg: colors.warning[100], fg: colors.warning[700], dot: colors.warning[500] };
    case "REFUNDED":
      return { bg: colors.info[100], fg: colors.info[700], dot: colors.info[500] };
    case "CANCELLED":
      return { bg: colors.gray[100], fg: colors.gray[500], dot: colors.gray[400] };
    default:
      return { bg: colors.gray[100], fg: colors.gray[500], dot: colors.gray[400] };
  }
};

function InvoicePill({
  invoicesByEvent,
  billingInvoiceUid,
  latestInvoice,
  onOpen,
}: {
  invoicesByEvent: ReturnType<typeof useGetInvoicesByJobEvent>["data"];
  billingInvoiceUid?: string | null;
  /** From list, GET /invoices/:billingUid, or GET ?jobEventUid= when the list endpoint fails */
  latestInvoice?: IInvoice | null;
  onOpen: (e: React.MouseEvent) => void;
}) {
  const latest = latestInvoice ?? invoicesByEvent?.[0];
  if (!latest && !billingInvoiceUid) return null;
  const shortUid = (uid?: string | null) => (uid ? uid.slice(0, 8) : "—");

  if (!latest && billingInvoiceUid) {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(e as any);
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: 32,
          pl: 1,
          pr: 0.75,
          borderRadius: "8px",
          bgcolor: alphaVar(colors.gray[100], 0.45),
          cursor: "pointer",
          transition: "all 0.15s ease",
          userSelect: "none",
          "&:hover": { bgcolor: colors.gray[100], boxShadow: `0 0 0 1px ${alphaVar(colors.gray[200], 0.6)}` },
          "&:active": { transform: "scale(0.97)" },
        }}
      >
        <ReceiptLongOutlinedIcon sx={{ fontSize: 14, color: colors.gray[400], opacity: 0.7 }} />
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: colors.gray[700],
            lineHeight: 1,
            letterSpacing: "-0.01em",
          }}
        >
          Invoice
        </Typography>
        <Chip
          label="Linked"
          size="small"
          sx={{
            height: 18,
            fontSize: "0.58rem",
            fontWeight: 500,
            bgcolor: alphaVar(colors.gray[500], 0.12),
            color: colors.gray[700],
            border: "none",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
        <Typography
          sx={{
            fontSize: "0.58rem",
            fontWeight: 600,
            color: colors.gray[500],
            lineHeight: 1,
            letterSpacing: "0.02em",
          }}
        >
          {shortUid(billingInvoiceUid)}
        </Typography>
        <ChevronRightIcon sx={{ fontSize: 14, color: colors.gray[300], ml: -0.25 }} />
      </Box>
    );
  }

  const status = getInvoiceStatus(latest!);
  const { bg, fg, dot } = statusPillColor(status);
  const amount = latest!.totalCost?.total?.value ?? 0;
  const count = invoicesByEvent!.length;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(e as any);
        }
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 32,
        pl: 1,
        pr: 0.75,
        borderRadius: "8px",
        bgcolor: alphaVar(colors.gray[100], 0.45),
        cursor: "pointer",
        transition: "all 0.15s ease",
        userSelect: "none",
        "&:hover": { bgcolor: colors.gray[100], boxShadow: `0 0 0 1px ${alphaVar(colors.gray[200], 0.6)}` },
        "&:active": { transform: "scale(0.97)" },
      }}
    >
      <ReceiptLongOutlinedIcon sx={{ fontSize: 14, color: colors.gray[400], opacity: 0.7 }} />
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          bgcolor: dot,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: colors.gray[700],
          lineHeight: 1,
          letterSpacing: "-0.01em",
        }}
      >
        ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Typography>
      <Chip
        label={getInvoiceStatusLabel(status)}
        size="small"
        sx={{
          height: 18,
          fontSize: "0.58rem",
          fontWeight: 500,
          bgcolor: alphaVar(fg, 0.12),
          color: fg,
          border: "none",
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
      {count > 1 && <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: colors.gray[300], lineHeight: 1 }}>+{count - 1}</Typography>}
      <Typography
        sx={{
          fontSize: "0.58rem",
          fontWeight: 600,
          color: colors.gray[500],
          lineHeight: 1,
          letterSpacing: "0.02em",
        }}
      >
        {shortUid(latest?.uid)}
      </Typography>
      <ChevronRightIcon sx={{ fontSize: 14, color: colors.gray[300], ml: -0.25 }} />
    </Box>
  );
}
